import {
  type SelectChangeEvent,
  Stack,
  //Switch,
  //SwitchProps,
  styled,
  Modal,
  Radio,
  RadioProps,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  checkboxClasses,
} from '@mui/material'
import { useState } from 'react'
import type { Definition } from '../../../types'
import MultipleSelect from './MultipleSelect'
import closeIcon from '../images/close-icon-light-mobile.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft'
import Button from '../../shared/Button'
import { Color } from '../../../constants'
import { FILTER_BUTTON_STYLES } from './constants'

const CustomRadio = styled((props: RadioProps) => (
  <Radio focusRipple={false} {...props} />
))(() => ({
  color: '#E9E7E1',
  padding: '2px 9px',
  '&.Mui-focusVisible': {
    outline: 'none',
  },
  '&.Mui-checked': {
    color: '#E9E7E1',
  },
}))

// const CustomSwitch = styled((props: SwitchProps) => (
//   <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
// ))(({ theme }) => ({
//   width: 42,
//   height: 23,
//   padding: 0,
//   '& .MuiSwitch-switchBase': {
//     padding: 0,
//     margin: 4,
//     transitionDuration: '300ms',
//     '&.Mui-checked': {
//       transform: 'translateX(16px)',
//       color: '#fff',
//       paddingLeft: 4,
//       '& + .MuiSwitch-track': {
//         backgroundColor: '#646464',
//         opacity: 1,
//         border: 0,
//       },
//       '&.Mui-disabled + .MuiSwitch-track': {
//         opacity: 0.5,
//       },
//     },
//     '&.Mui-focusVisible .MuiSwitch-thumb': {
//       color: '#222222',
//       border: '6px solid #222222',
//     },
//     '&.Mui-disabled .MuiSwitch-thumb': {
//       color: '#222222',
//     },
//     '&.Mui-disabled + .MuiSwitch-track': {
//       opacity: 0.7,
//     },
//   },
//   '& .MuiSwitch-thumb': {
//     boxSizing: 'border-box',
//     width: 15,
//     height: 15,
//     color: '#e9e7e1',
//   },
//   '& .MuiSwitch-track': {
//     borderRadius: 11,
//     backgroundColor: '#222222',
//     opacity: 1,
//     transition: theme.transitions.create(['background-color'], {
//       duration: 500,
//     }),
//     border: `1px solid #e9e7e1`,
//     width: '100%',
//   },
// }))

type FilterSectionMobileProps = {
  display: string
  setDisplay: (display: string) => void
  handleDisplay: (display: string) => void
  open: boolean
  definitions: Definition[]
  globalGeoScopeOptions: string[]
  onSelectedGeoScopeOptionsChange: (options: string[]) => void
  globalFocusOptions: string[]
  onSelectedFocusOptionsChange: (options: string[]) => void
  globalStrategyOptions: string[]
  onSelectedStrategyOptionsChange: (options: string[]) => void
  globalLiableInstitution: string
  onLiableInstitutionChange: (option: string) => void
  globalInitCommTask: boolean
  onInitCommTaskChange: (checked: boolean) => void
  globalStageOptions: string[]
  onSelectedStageOptionsChange: (options: string[]) => void
  globalRepairOptions: string[]
  onSelectedRepairOptionsChange: (options: string[]) => void
  globalInitiativeTypeCheckboxes: { [key: string]: boolean }
  onSelectedInitiativeTypeCheckboxesChange: (checkboxes: {
    [key: string]: boolean
  }) => void
  globalInitiativeTypeOptions: string[]
  onSelectedInitiativeTypeOptionsChange: (options: string[]) => void
  onToggleFilterModalVisibility: () => void
}

function FilterSectionMobile({
  display,
  setDisplay,
  handleDisplay,
  open,
  definitions,
  globalGeoScopeOptions,
  onSelectedGeoScopeOptionsChange,
  globalFocusOptions,
  onSelectedFocusOptionsChange,
  globalStrategyOptions,
  onSelectedStrategyOptionsChange,
  globalLiableInstitution,
  onLiableInstitutionChange,
  globalInitCommTask,
  onInitCommTaskChange,
  globalStageOptions,
  onSelectedStageOptionsChange,
  globalRepairOptions,
  onSelectedRepairOptionsChange,
  globalInitiativeTypeCheckboxes,
  onSelectedInitiativeTypeCheckboxesChange,
  globalInitiativeTypeOptions,
  onSelectedInitiativeTypeOptionsChange,
  onToggleFilterModalVisibility,
}: FilterSectionMobileProps) {
  const [selectedGeoScopeOptions, setSelectedGeoScopeOptions] = useState<
    string[]
  >(globalGeoScopeOptions)
  const [selectedFocusOptions, setSelectedFocusOptions] =
    useState<string[]>(globalFocusOptions)
  const [selectedStrategyOptions, setSelectedStrategyOptions] = useState<
    string[]
  >(globalStrategyOptions)
  const [liableInstitution, setLiableInstitution] = useState<string>(
    globalLiableInstitution
  )
  const [isInitCommTask, setIsInitCommTask] =
    useState<boolean>(globalInitCommTask)

  // Get menu options from the definitions file
  const geoScopeOptions = definitions.filter((def) =>
    def.id.startsWith('geo_scope:')
  )
  const focusOptions = definitions.filter((def) => def.id.startsWith('focus:'))

  const strategyOptions = definitions.filter((def) =>
    def.id.startsWith('strategy:')
  )

  const [selectedStageOptions, setSelectedStageOptions] = useState<string[]>(
    globalGeoScopeOptions
  )
  const [selectedRepairOptions, setSelectedRepairOptions] =
    useState<string[]>(globalFocusOptions)
  // const [selectedInitiativeTypeOptions, setSelectedInitiativeTypeOptions] =
  //   useState<string[]>(globalInitiativeTypeOptions)
  const stageOptions = definitions.filter((def) => def.id.startsWith('stage:'))

  const repairOptions = definitions.filter((def) =>
    def.id.startsWith('repair:')
  )

  const initiativeTypeOptions = definitions.filter((def) =>
    def.id.startsWith('initiative_type:')
  )

  const handleGeoScopeChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    setSelectedGeoScopeOptions(
      typeof value === 'string' ? value.split(',') : value
    )
  }

  const handleFocusAreaChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    setSelectedFocusOptions(
      typeof value === 'string' ? value.split(',') : value
    )
  }

  const handleStrategyChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    setSelectedStrategyOptions(
      typeof value === 'string' ? value.split(',') : value
    )
  }

  // const handleIsInitCommTaskChange = (
  //   _event: ChangeEvent<HTMLInputElement>,
  //   checked: boolean
  // ) => {
  //   setIsInitCommTask(checked)
  // }

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLiableInstitution((event.target as HTMLInputElement).value)
  }

  const handleStageChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    setSelectedStageOptions(
      typeof value === 'string' ? value.split(',') : value
    )
  }

  const handleRepairChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    setSelectedRepairOptions(
      typeof value === 'string' ? value.split(',') : value
    )
  }

  const [
    selectedInitiativeTypeCheckboxes,
    setSelectedInitiativeTypeCheckboxes,
  ] = useState<{ [key: string]: boolean }>({
    government: true,
    academy: true,
    faith_based: true,
    descendant: true,
  })
  const handleInitiativeTypeChange = (event: any) => {
    setSelectedInitiativeTypeCheckboxes({
      ...selectedInitiativeTypeCheckboxes,
      [event.target.name]: event.target.checked,
    })
  }

  const formatSelectedInitiativeTypeOptions = (
    selectedInitiativeTypeCheckboxes:
      | { [s: string]: unknown }
      | ArrayLike<unknown>
  ) => {
    return Object.entries(selectedInitiativeTypeCheckboxes)
      .filter(([key, value]) => value === true)
      .map(([key]) => key)
  }

  const onApplyFilters = () => {
    onSelectedGeoScopeOptionsChange(selectedGeoScopeOptions)
    onSelectedFocusOptionsChange(selectedFocusOptions)
    onSelectedStrategyOptionsChange(selectedStrategyOptions)
    onLiableInstitutionChange(liableInstitution)
    onInitCommTaskChange(isInitCommTask)
    onSelectedStageOptionsChange(selectedStageOptions)
    onSelectedRepairOptionsChange(selectedRepairOptions)
    onSelectedInitiativeTypeCheckboxesChange(selectedInitiativeTypeCheckboxes)
    onSelectedInitiativeTypeOptionsChange(
      formatSelectedInitiativeTypeOptions(
        selectedInitiativeTypeCheckboxes
      ) as any
    )
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'filter_applied', {
        filter_type: 'mobile_filters',
        geo_scope: selectedGeoScopeOptions.join(', ') || 'none',
        focus_area: selectedFocusOptions.join(', ') || 'none',
        strategy: selectedStrategyOptions.join(', ') || 'none',
        liable_institution: liableInstitution,
        stage: selectedStageOptions.join(', ') || 'none',
        repair: selectedRepairOptions.join(', ') || 'none',
      })
    }
    onToggleFilterModalVisibility()
  }

  const onClose = () => {
    setSelectedGeoScopeOptions(globalGeoScopeOptions)
    setSelectedFocusOptions(globalFocusOptions)
    setSelectedStrategyOptions(globalStrategyOptions)
    setLiableInstitution(globalLiableInstitution)
    setIsInitCommTask(globalInitCommTask)
    setSelectedStageOptions(globalStageOptions)
    setSelectedRepairOptions(globalRepairOptions)
    setSelectedInitiativeTypeCheckboxes(globalInitiativeTypeCheckboxes)

    onToggleFilterModalVisibility()
  }

  const handleMobileDisplay = (display: string) => {
    handleDisplay(display)
    //the back button needs to clear the filters locally
    setSelectedGeoScopeOptions([])
    setSelectedFocusOptions([])
    setSelectedStrategyOptions([])
    setLiableInstitution('all')

    setSelectedStageOptions([])
    setSelectedRepairOptions([])
    setSelectedInitiativeTypeCheckboxes({
      government: true,
      academy: true,
      faith_based: true,
      descendant: true,
    })
  }
  const showBoth = display === 'all'
  const showOrgs = display === 'orgs'
  const showInits = display === 'inits'

  const mainColor: string = '#E9E7E1'
  const checkmarkSvg = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
    <path fill-rule="evenodd" clip-rule="evenodd"
      d="M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z"
      fill="${mainColor}"/>
  </svg>
`
  const encodedCheckmarkSvg = encodeURIComponent(checkmarkSvg)

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="filter-section-mobile-modal"
      aria-describedby="a filter section modal for mobile screens"
    >
      <div
        className="absolute w-[377px] top-[50%] left-[50%] bg-transparent p-6 translate-x-[-50%] translate-y-[-50%] bg-filter-section-bg-mobile focus-visible:outline-none flex flex-col"
        style={{
          maxHeight: `min(calc(100dvh - 32px), 732px)`,
          height: `min(calc(100dvh - 32px), 732px)`,
        }}
      >
        <div className="relative w-full flex-1 min-h-0 flex flex-col">
          <Stack className="gap-6 flex-1 min-h-0 overflow-y-auto">
            <img
              src={closeIcon}
              alt="the letter x"
              role="button"
              className="flex self-end cursor-pointer"
              onClick={onClose}
            />
            {showBoth && (
              <Stack className="gap-3 justify-center items-center h-[80%]">
                <p className="font-decimal text-[17px] text-white font-semibold text-center">
                  Select Reparations Movement
                </p>
                <Button
                  onClick={() => {
                    if (typeof window.gtag === 'function') {
                      window.gtag('event', 'filter_category_selected', { category: 'initiatives' })
                    }
                    setDisplay('inits')
                  }}
                  textColor={Color.White}
                  backgroundColor={Color.Black}
                  styles={FILTER_BUTTON_STYLES}
                >
                  <Stack direction="row" className="items-center">
                    <div className="rounded-full flex bg-mediumyellow min-w-[12px] max-w-[12px] h-[12px] mr-2"></div>
                    <span>Initiatives</span>
                  </Stack>
                </Button>
                <p className="font-decimal text-[14px] text-white font-semibold">
                  or
                </p>
                <Button
                  onClick={() => {
                    if (typeof window.gtag === 'function') {
                      window.gtag('event', 'filter_category_selected', { category: 'movement_organizations' })
                    }
                    setDisplay('orgs')
                  }}
                  textColor={Color.White}
                  backgroundColor={Color.Black}
                  styles={FILTER_BUTTON_STYLES}
                >
                  <Stack direction="row" className="items-center">
                    <div className="rounded-full flex bg-green min-w-[12px] max-w-[12px] h-[12px] mr-2"></div>
                    <span>Movement Organizations</span>
                  </Stack>
                </Button>
              </Stack>
            )}
            {showOrgs && (
              <Stack className="gap-5 text-white">
                <button
                  className="font-decimal text-[12px] bg-transparent border border-solid border-white flex justify-center items-center px-2 py-1 cursor-pointer w-max "
                  type="button"
                  onClick={() => handleMobileDisplay('all')}
                >
                  <FontAwesomeIcon
                    icon={faChevronLeft}
                    className="text-white"
                  />
                  <p className="ml-1">BACK</p>
                </button>
                <p className="text-[18px] uppercase font-semibold mt-2">
                  Movement Orgs
                </p>
                <MultipleSelect
                  placeholder={
                    definitions
                      .find((def) => def.id === 'geo_scope')
                      ?.display_label.toUpperCase() || ''
                  }
                  options={geoScopeOptions}
                  selectedOptions={selectedGeoScopeOptions}
                  description={
                    definitions.find((def) => def.id === 'geo_scope')
                      ?.definition || ''
                  }
                  onChange={handleGeoScopeChange}
                  onChipClick={(option) => {
                    setSelectedGeoScopeOptions((prevState) => {
                      return prevState.filter((item) => item !== option)
                    })
                  }}
                  isMobile
                  hideInfoTooltip
                />
                <MultipleSelect
                  placeholder={
                    definitions
                      .find((def) => def.id === 'focus')
                      ?.display_label.toUpperCase() || ''
                  }
                  options={focusOptions}
                  selectedOptions={selectedFocusOptions}
                  description={
                    definitions.find((def) => def.id === 'focus')?.definition ||
                    ''
                  }
                  onChange={handleFocusAreaChange}
                  onChipClick={(option) => {
                    setSelectedFocusOptions((prevState) => {
                      return prevState.filter((item) => item !== option)
                    })
                  }}
                  isMobile
                  hideInfoTooltip
                />
                <MultipleSelect
                  placeholder={
                    definitions
                      .find((def) => def.id === 'strategy')
                      ?.display_label.toUpperCase() || ''
                  }
                  options={strategyOptions}
                  selectedOptions={selectedStrategyOptions}
                  description={
                    definitions.find((def) => def.id === 'strategy')
                      ?.definition || ''
                  }
                  onChange={handleStrategyChange}
                  onChipClick={(option) => {
                    setSelectedStrategyOptions((prevState) => {
                      return prevState.filter((item) => item !== option)
                    })
                  }}
                  isMobile
                  hideInfoTooltip
                />
                <Stack>
                  <p className="font-decimal text-[18px] text-white font-semibold mb-2">
                    {definitions
                      .find((def) => def.id === 'target')
                      ?.display_label.toUpperCase() || ''}
                  </p>
                  <RadioGroup
                    aria-labelledby="radio-buttons-group-label"
                    defaultValue="all"
                    name="radio-buttons-group"
                    className="font-decimal text-white text-[14px] leading-[14px]"
                    onChange={handleRadioChange}
                    value={liableInstitution}
                  >
                    <FormControlLabel
                      value="all"
                      control={<CustomRadio />}
                      label="All"
                    />
                    <FormControlLabel
                      value="government"
                      control={<CustomRadio />}
                      label="Government"
                    />
                    <FormControlLabel
                      value="institutions"
                      control={<CustomRadio />}
                      label="Institutions"
                    />
                  </RadioGroup>
                </Stack>
              </Stack>
            )}
            {showInits && (
              <Stack className="gap-5 text-white">
                <button
                  className="font-decimal text-[12px] bg-transparent border border-solid border-white flex justify-center items-center px-2 py-1 cursor-pointer w-max "
                  type="button"
                  onClick={() => handleMobileDisplay('all')}
                >
                  <FontAwesomeIcon
                    icon={faChevronLeft}
                    className="text-white"
                  />
                  <p className="ml-1">BACK</p>
                </button>
                <p className="text-[18px] uppercase font-semibold mt-2">
                  Initiatives
                </p>
                <MultipleSelect
                  placeholder={
                    definitions
                      .find((def) => def.id === 'stage')
                      ?.display_label.toUpperCase() || ''
                  }
                  options={stageOptions}
                  selectedOptions={selectedStageOptions}
                  description={
                    definitions.find((def) => def.id === 'stage')?.definition ||
                    ''
                  }
                  onChange={handleStageChange}
                  onChipClick={(option) => {
                    setSelectedStageOptions((prevState) => {
                      return prevState.filter((item) => item !== option)
                    })
                  }}
                  isMobile
                  hideInfoTooltip
                />
                <MultipleSelect
                  placeholder={
                    definitions
                      .find((def) => def.id === 'repair')
                      ?.display_label.toUpperCase() || ''
                  }
                  options={repairOptions}
                  selectedOptions={selectedRepairOptions}
                  description={
                    definitions.find((def) => def.id === 'repair')
                      ?.definition || ''
                  }
                  onChange={handleRepairChange}
                  onChipClick={(option) => {
                    setSelectedRepairOptions((prevState) => {
                      return prevState.filter((item) => item !== option)
                    })
                  }}
                  isMobile
                  hideInfoTooltip
                />

                <Stack>
                  <p className="font-decimal text-[18px] text-white font-semibold mb-2">
                    {definitions
                      .find((def) => def.id === 'initiative_type')
                      ?.display_label.toUpperCase() || ''}
                  </p>
                  {initiativeTypeOptions.map((option) => {
                    return (
                      <FormControlLabel
                        key={option.id}
                        control={
                          <Checkbox
                            onChange={handleInitiativeTypeChange}
                            checked={
                              selectedInitiativeTypeCheckboxes[
                                option.id.split(':')[1]
                              ]
                            }
                            checkedIcon={<span />}
                            icon={<span />}
                            name={option.id.split(':')[1]}
                            sx={{
                              [`&:not(.${checkboxClasses.checked})`]: {
                                '& input + *': {
                                  boxShadow: `inset 0 0 0 1px ${mainColor}, inset 0 -1px 0 ${mainColor}`,
                                  backgroundColor: '#212121',
                                  width: '16px',
                                  height: '16px',
                                },
                              },
                              [`&.${checkboxClasses.checked}`]: {
                                '& input + *': {
                                  boxShadow: `inset 0 0 0 1px ${mainColor}, inset 0 -1px 0 ${mainColor}`,
                                  backgroundColor: '#212121',
                                  backgroundImage:
                                    'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
                                  '&:before': {
                                    display: 'block',
                                    width: '16px',
                                    height: '16px',
                                    backgroundImage: `url("data:image/svg+xml,${encodedCheckmarkSvg}")`,
                                    content: '""',
                                  },
                                },
                              },
                            }}
                          />
                        }
                        label={option.display_label}
                        sx={{
                          marginLeft: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            color: 'white',
                          },
                        }}
                      />
                    )
                  })}
                </Stack>
              </Stack>
            )}
            {!showBoth && (
              <div
                role="button"
                className="uppercase bg-green w-full text-white text-[18px] py-4 font-decimal font-bold align-center h-[48px] flex items-center justify-center"
                onClick={onApplyFilters}
              >
                Apply
              </div>
            )}
          </Stack>
        </div>
      </div>
    </Modal>
  )
}

export default FilterSectionMobile
//OLD CODE for initcommtask toggle

/* <Stack className="gap-4">
<p className="text-[18px] text-white font-semibold font-decimal w-[280px]">
  {definitions
    .find((def) => def.id === 'initiative_commission_taskforce')
    ?.display_label.toUpperCase() || ''}
</p>
<CustomSwitch
  checked={isInitCommTask}
  onChange={handleIsInitCommTaskChange}
/>
</Stack> */
